/*
 * Copyright 2022 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useApiHolder } from '@mainstage/core-plugin-api';
import { ResponseError } from '@mainstage/errors';
import { JsonObject, JsonValue } from '@mainstage/types';
import { makeStyles } from '@material-ui/core/styles';
import { Component, ReactNode, useState } from 'react';
import useDebounce from 'react-use/lib/useDebounce';
import yaml from 'yaml';
import { FieldExtensionOptions } from '../../extensions/types';
import { LayoutOptions } from '../../layouts';
import { TemplateParameterSchema } from '../../types';
import {
  MultiStepJsonForm,
  reviewStep,
  useDefaultValidations,
} from '../MultiStepJsonForm';
import { createValidator, createValidatorAsync } from '../TemplatePage';
import { useDirectoryEditor } from './DirectoryEditorContext';
import { useDryRun } from './DryRunContext';
import { useFormSteps } from '../MultiStepJsonForm/useFormSteps';
import { LinearProgress } from '@material-ui/core';

const useStyles = makeStyles({
  containerWrapper: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  container: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    overflow: 'auto',
  },
});

interface ErrorBoundaryProps {
  invalidator: unknown;
  setErrorText(errorText: string | undefined): void;
  children: ReactNode;
}

interface ErrorBoundaryState {
  shouldRender: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state = {
    shouldRender: true,
  };

  componentDidUpdate(prevProps: { invalidator: unknown }) {
    if (prevProps.invalidator !== this.props.invalidator) {
      // eslint-disable-next-line
      this.setState({ shouldRender: true });
    }
  }

  componentDidCatch(error: Error) {
    this.props.setErrorText(error.message);
    this.setState({ shouldRender: false });
  }

  render() {
    return this.state.shouldRender ? this.props.children : null;
  }
}

interface TemplateEditorFormProps {
  content?: string;
  /** Setting this to true will cause the content to be parsed as if it is the template entity spec */
  contentIsSpec?: boolean;
  /** This indicates if the template is an edit template */
  isEditTemplate?: boolean;
  /** Hide the "Try it" button in the review step */
  hideTryItButton?: boolean;
  data: JsonObject;
  onUpdate: (data: JsonObject) => void;
  setErrorText: (errorText?: string) => void;

  onDryRun?: (data: JsonObject) => Promise<void>;
  fieldExtensions?: FieldExtensionOptions<any, any>[];
  layouts?: LayoutOptions[];
}

function isJsonObject(value: JsonValue | undefined): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const DEFAULT_FIELD_EXTENSIONS: FieldExtensionOptions<any, any>[] = [];

/** Shows the a template form that is parsed from the provided content */
export function TemplateEditorForm(props: TemplateEditorFormProps) {
  const {
    content,
    contentIsSpec,
    isEditTemplate: isEditTemplateProp,
    hideTryItButton,
    data,
    onUpdate,
    onDryRun,
    setErrorText,
    fieldExtensions = DEFAULT_FIELD_EXTENSIONS,
    layouts,
  } = props;
  const classes = useStyles();
  const apiHolder = useApiHolder();

  const [steps, setSteps] = useState<TemplateParameterSchema['steps']>();
  const [isEditTemplate, setIsEditTemplate] = useState(false);

  const { customFieldValidatorsAsync, customFieldValidatorsSync } =
    useDefaultValidations(fieldExtensions);

  useDebounce(
    () => {
      try {
        if (!content) {
          setSteps(undefined);
          return;
        }
        const parsed: JsonValue = yaml.parse(content);

        if (!isJsonObject(parsed)) {
          setSteps(undefined);
          return;
        }

        let rootObj = parsed;
        if (!contentIsSpec) {
          const kind = String(parsed.kind).toLocaleLowerCase('en-US');
          const isTemplate = kind === 'template' || kind === 'edittemplate';
          if (!isTemplate) {
            setSteps(undefined);
            setIsEditTemplate(false);
            return;
          }
          setIsEditTemplate(kind === 'edittemplate');

          rootObj = isJsonObject(parsed.spec) ? parsed.spec : {};
        } else {
          // When contentIsSpec is true, use this prop to determine if it's an edit template
          setIsEditTemplate(isEditTemplateProp ?? false);
        }

        const { parameters } = rootObj;

        if (!Array.isArray(parameters)) {
          setErrorText('Template parameters must be an array');
          setSteps(undefined);
          return;
        }

        setErrorText();
        setSteps(
          parameters.flatMap(param =>
            isJsonObject(param)
              ? [
                  {
                    title: String(param.title),
                    schema: param,
                    asyncValidate: createValidatorAsync(
                      param,
                      customFieldValidatorsAsync,
                      { apiHolder },
                    ),
                    customValidate: createValidator(
                      param,
                      customFieldValidatorsSync,
                      {
                        apiHolder,
                      },
                    ),
                  },
                ]
              : [],
          ),
        );
      } catch (e) {
        setErrorText(e.message);
      }
    },
    250,
    [contentIsSpec, content, apiHolder, isEditTemplateProp],
  );

  const { steps: processedSteps, loading: stepsLoading } = useFormSteps(steps, {
    layouts,
  });

  if (!steps) {
    return null;
  }

  let onReviewNext: () => Promise<void>;
  if (isEditTemplate) {
    onReviewNext = async () => {};
  } else if (onDryRun) {
    onReviewNext = () => onDryRun(data);
  } else {
    onReviewNext = async () => {};
  }

  let tryItButtonText = '';
  if (!hideTryItButton) {
    tryItButtonText = isEditTemplate ? 'Done' : 'Try it';
  }

  // Determine review step title: use "Review" when button is hidden OR when it's an edit template
  const reviewStepTitle =
    hideTryItButton || isEditTemplate ? 'Review' : 'Review and Try It';

  return (
    <div className={classes.containerWrapper}>
      <div className={classes.container}>
        <ErrorBoundary invalidator={steps} setErrorText={setErrorText}>
          {stepsLoading && <LinearProgress data-testid="loading-progress" />}
          {!stepsLoading && processedSteps && (
            <MultiStepJsonForm
              steps={processedSteps}
              fields={fieldExtensions}
              formData={data}
              onChange={e => onUpdate(e.formData)}
              additionalSteps={[
                reviewStep(
                  reviewStepTitle,
                  tryItButtonText,
                  data,
                  processedSteps,
                  () => {},
                  onReviewNext,
                  // If not an edit template and the onDryRun function is provided (so we are in template directory tool), stay on the review step after clicking the button to allow executing multiple dry runs
                  !isEditTemplate && !!onDryRun,
                ),
              ]}
            />
          )}
        </ErrorBoundary>
      </div>
    </div>
  );
}

/** A version of the TemplateEditorForm that is connected to the DirectoryEditor and DryRun contexts */
export function TemplateEditorFormDirectoryEditorDryRun(
  props: Pick<
    TemplateEditorFormProps,
    'setErrorText' | 'fieldExtensions' | 'layouts'
  > & {
    /** When provided, lifts form data state to the parent. Falls back to internal state when omitted. */
    data?: JsonObject;
    onUpdate?: (data: JsonObject) => void;
  },
) {
  const { setErrorText, fieldExtensions = [], layouts } = props;
  const dryRun = useDryRun();

  const directoryEditor = useDirectoryEditor();
  const { selectedFile } = directoryEditor;

  const [internalData, setInternalData] = useState<JsonObject>({});
  const data = props.data ?? internalData;
  const setData = props.onUpdate ?? setInternalData;

  const handleDryRun = async () => {
    if (!selectedFile) {
      return;
    }

    try {
      await dryRun.execute({
        templateContent: selectedFile.content as string,
        values: data,
        files: directoryEditor.files,
      });
      setErrorText();
    } catch (e) {
      if (e instanceof ResponseError && e.statusCode === 500) {
        const message = `Could not dry run "${selectedFile.path}". Make sure you loaded a folder containing the YAML template definition file at the root level.`;
        setErrorText(message);
        throw new Error(message);
      }
      setErrorText(String((e as Error).cause || e));
      throw e;
    }
  };

  const content =
    selectedFile && selectedFile.path.match(/\.ya?ml$/)
      ? selectedFile.content
      : undefined;

  return (
    <TemplateEditorForm
      onDryRun={handleDryRun}
      fieldExtensions={fieldExtensions}
      setErrorText={setErrorText}
      content={content as string}
      data={data}
      onUpdate={setData}
      layouts={layouts}
    />
  );
}

TemplateEditorForm.DirectoryEditorDryRun =
  TemplateEditorFormDirectoryEditorDryRun;
